import { SortableList, Text } from '@southfields-digital/mpxlive-components';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DisplayHub, { IDisplayHubProps } from 'src/components/DisplayHub';
import useKeypress from 'src/hooks/useKeypress';
import { StateType } from 'src/redux/reducers';
import { PlaylistGetAllStateType } from 'src/redux/reducers/playlist';
import {
  REQUEST_DELETE_PLAYLIST_DISPLAY_HUB,
  REQUEST_DUPLICATE_PLAYLIST_DISPLAY_HUB,
  REQUEST_REORDER_PLAYLIST_DISPLAY_HUB,
  REQUEST_SET_SELECTED_PLAYLIST_DISPLAY_HUB,
} from 'src/redux/reducers/playlistDisplayHub';

import styles from './DisplayHubs.module.scss';

export default function DisplayHubs() {
  const dispatch = useDispatch();

  const { playlistDisplayHubs, selectedPlaylistDisplayHub } = useSelector(
    (state: StateType) => state.playlistDisplayHub
  );
  const { selectedId: playlistId }: PlaylistGetAllStateType = useSelector(
    (state: StateType) => state.playlist.getAll
  );

  const displayHubs = useMemo(
    () => playlistDisplayHubs?.filter((displayHub) => displayHub.playlist.id === playlistId),
    [playlistDisplayHubs, playlistId]
  );

  const handleDeleteDisplayHub = (displayHub: PlaylistDisplayHub) =>
    dispatch({
      type: REQUEST_DELETE_PLAYLIST_DISPLAY_HUB,
      payload: { playlistId: displayHub.playlist.id, playlistDisplayHubId: displayHub.id },
    });

  const handleDuplicateDisplayHub = (displayHub: PlaylistDisplayHub) =>
    dispatch({
      type: REQUEST_DUPLICATE_PLAYLIST_DISPLAY_HUB,
      payload: { playlistId: displayHub.playlist.id, playlistDisplayHubId: displayHub.id },
    });

  const handleClickDisplayHub = (displayHub: PlaylistDisplayHub) =>
    dispatch({
      type: REQUEST_SET_SELECTED_PLAYLIST_DISPLAY_HUB,
      payload: { playlistDisplayHub: displayHub, selectedPlaylistDisplayHub },
    });

  const handleSortDisplayHubs = (sortedValues: IDisplayHubProps[]) => {
    const sortedDisplayHubs: string[] = sortedValues.map((item) => item.id);
    dispatch({
      type: REQUEST_REORDER_PLAYLIST_DISPLAY_HUB,
      payload: {
        playlistId,
        playlistDisplayHubIds: sortedDisplayHubs,
      },
    });
  };

  const selectNextDisplayHub = () => {
    if (!displayHubs?.length) return;

    const index = displayHubs.findIndex(
      (displayHub) => displayHub.id === selectedPlaylistDisplayHub?.id
    );
    const nextDisplayHub = displayHubs[index + 1];
    if (nextDisplayHub) {
      dispatch({
        type: REQUEST_SET_SELECTED_PLAYLIST_DISPLAY_HUB,
        payload: { playlistDisplayHub: nextDisplayHub, selectedPlaylistDisplayHub },
      });
    }
  };

  const selectPreviousDisplayHub = () => {
    if (!displayHubs?.length) return;

    const index = displayHubs.findIndex(
      (displayHub) => displayHub.id === selectedPlaylistDisplayHub?.id
    );
    const previousDisplayHub = displayHubs[index - 1];
    if (previousDisplayHub) {
      dispatch({
        type: REQUEST_SET_SELECTED_PLAYLIST_DISPLAY_HUB,
        payload: { playlistDisplayHub: previousDisplayHub, selectedPlaylistDisplayHub },
      });
    }
  };

  useKeypress('PageUp', {}, selectPreviousDisplayHub);
  useKeypress('PageDown', {}, selectNextDisplayHub);

  const items: IDisplayHubProps[] = useMemo(() => {
    if (!displayHubs) return [];

    return displayHubs.map((displayHub) => ({
      id: displayHub.id,
      text: displayHub.name,
      subText:
        displayHub.storyCount === 1
          ? `${displayHub.storyCount} story`
          : `${displayHub.storyCount} stories`,
      active: selectedPlaylistDisplayHub?.id === displayHub.id,
      variant: 'default',
      onClick: () => handleClickDisplayHub(displayHub),
      onDuplicate: () => handleDuplicateDisplayHub(displayHub),
      onDelete: () => handleDeleteDisplayHub(displayHub),
    }));
  }, [displayHubs, selectedPlaylistDisplayHub]);

  if (!playlistId) {
    return (
      <div className={styles.NoContent}>
        <Text as="p">Please select a playlist</Text>
      </div>
    );
  }

  if (!displayHubs?.length) {
    return (
      <div className={styles.NoContent}>
        <Text as="p">No Display Hubs</Text>
      </div>
    );
  }

  return (
    <div className={styles.Container}>
      <SortableList
        activeId={selectedPlaylistDisplayHub?.id}
        items={items}
        setItems={handleSortDisplayHubs}
        Component={DisplayHub}
      />
    </div>
  );
}
